import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  Card,
  CardActionArea,
  CardContent,
  Divider
} from '@mui/material';

import { blueGrey, grey } from '@mui/material/colors';

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const SEARCH_DATA = [
  // 서비스
  {
    category: '서비스',
    title: 'Daclair (다끌레어)',
    description: '다끌레어 플랫폼 서비스. 환경 데이터 기반의 ESG 관리 솔루션을 제공합니다.',
    url: '/services/daclair',
    keywords: ['다끌레어', 'daclair', '서비스', 'esg', '환경', '플랫폼'],
  },
  {
    category: '서비스',
    title: 'ENV-REPORT',
    description: '기업 맞춤형 환경 리포트 서비스. 탄소 배출량 및 환경 성과를 분석합니다.',
    url: '/services/env-report',
    keywords: ['env-report', '환경', '리포트', '탄소', '배출', '분석'],
  },
  {
    category: '서비스',
    title: 'ENV-AD',
    description: '친환경 광고 서비스. 탄소 중립 캠페인을 위한 광고 솔루션입니다.',
    url: '/services/env-ad',
    keywords: ['env-ad', '환경', '광고', '탄소중립', '캠페인'],
  },
  {
    category: '서비스',
    title: '탄소중립마켓 (VCM)',
    description: '자발적 탄소 시장(VCM) 기반의 탄소 크레딧 거래 플랫폼입니다.',
    url: '/services/vcm',
    keywords: ['탄소중립', 'vcm', '마켓', '크레딧', '탄소'],
  },
  // 제품
  {
    category: '제품',
    title: '청소 카트',
    description: '다끌레어 스마트 청소 카트. 효율적인 공간 청소를 위한 제품입니다.',
    url: '/products/cleaningcart',
    keywords: ['청소', '카트', '제품', '스마트', '청소카트'],
  },
  {
    category: '제품',
    title: '텀블러 세척기',
    description: '다끌레어 자동 텀블러 세척기. 위생적이고 편리한 세척 솔루션입니다.',
    url: '/products/tumblerwasher',
    keywords: ['텀블러', '세척기', '제품', '자동', '위생'],
  },
  // 회사소개
  {
    category: '회사소개',
    title: 'Mission',
    description: '다끌레어의 미션과 비전. 지속 가능한 미래를 위한 환경 기업입니다.',
    url: '/company/mission',
    keywords: ['미션', 'mission', '비전', '회사', '소개', '환경'],
  },
  {
    category: '회사소개',
    title: 'Sustainability',
    description: '다끌레어의 지속가능경영. ESG 경영을 실천하는 환경 기업입니다.',
    url: '/company/sustainability',
    keywords: ['sustainability', '지속가능', 'esg', '경영', '회사'],
  },
  {
    category: '회사소개',
    title: '회사 위치',
    description: '다끌레어 오피스 위치 안내.',
    url: '/company/locations',
    keywords: ['위치', '오피스', '주소', '찾아오시는길', '회사'],
  },
  {
    category: '회사소개',
    title: '문의하기',
    description: '다끌레어에 문의하세요. 파트너십, 제품, 서비스 관련 문의를 받습니다.',
    url: '/company/contact',
    keywords: ['문의', '연락', '고객지원', '파트너십', '상담'],
  },
  // 고객지원
  {
    category: '고객지원',
    title: '공지사항',
    description: '다끌레어의 최신 공지사항과 소식을 확인하세요.',
    url: '/notices',
    keywords: ['공지', '공지사항', '소식', '알림', '뉴스'],
  },
  // 사용설명서
  {
    category: '사용설명서',
    title: '사용설명서 (준비 중)',
    description: '제품 및 서비스 사용설명서. 빠른 시일 내에 제공될 예정입니다.',
    url: '/manuals',
    keywords: ['사용설명서', '매뉴얼', '가이드', '사용법', '설명서'],
  },
];

const CATEGORY_ORDER = ['서비스', '제품', '회사소개', '고객지원', '사용설명서'];

function highlight(text, query) {
  if (!query) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} style={{ backgroundColor: '#fff176', borderRadius: '2px', padding: '0 1px' }}>{part}</mark>
      : part
  );
}

function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');

  useEffect(() => {
    const q = searchParams.get('q') || '';
    setQuery(q);
  }, [searchParams]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (val.trim()) {
      setSearchParams({ q: val });
    } else {
      setSearchParams({});
    }
  };

  const filteredData = query.trim()
    ? SEARCH_DATA.filter((item) => {
        const q = query.trim().toLowerCase();
        return (
          item.title.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q) ||
          item.keywords.some((k) => k.toLowerCase().includes(q))
        );
      })
    : [];

  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const items = filteredData.filter((item) => item.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  const totalCount = filteredData.length;

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', backgroundColor: '#f9f9f9', py: 6 }}>
      <Container maxWidth="md">
        {/* 검색 헤더 */}
        <Stack spacing={1} alignItems="center" sx={{ mb: 5 }}>
          <Typography
            variant="h5"
            sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, color: blueGrey[900] }}>
            전체 검색
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: 'NotoSansKR', color: grey[500] }}>
            서비스, 제품, 회사소개, 공지사항을 통합 검색합니다
          </Typography>
        </Stack>

        {/* 검색 입력창 */}
        <TextField
          fullWidth
          autoFocus
          placeholder="검색어를 입력하세요"
          value={query}
          onChange={handleChange}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ color: blueGrey[400] }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              fontFamily: 'NotoSansKR',
              fontSize: '16px',
            },
          }}
          sx={{ mb: 4 }}
        />

        {/* 검색 결과 */}
        {query.trim() ? (
          <>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'NotoSansKR', color: grey[500], mb: 3 }}>
              <strong style={{ color: blueGrey[700] }}>"{query}"</strong> 검색 결과{' '}
              {totalCount}건
            </Typography>

            {totalCount === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 10,
                  color: grey[400],
                }}>
                <SearchOutlinedIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '15px' }}>
                  검색 결과가 없습니다
                </Typography>
                <Typography
                  sx={{ fontFamily: 'NotoSansKR', fontSize: '13px', mt: 1, color: grey[400] }}>
                  다른 검색어를 입력해보세요
                </Typography>
              </Box>
            ) : (
              <Stack spacing={4}>
                {Object.entries(grouped).map(([category, items]) => (
                  <Box key={category}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <Chip
                        label={category}
                        size="small"
                        sx={{
                          fontFamily: 'NotoSansKR',
                          fontWeight: 700,
                          fontSize: '12px',
                          backgroundColor: blueGrey[800],
                          color: '#fff',
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: grey[500], fontFamily: 'NotoSansKR' }}>
                        {items.length}건
                      </Typography>
                    </Stack>
                    <Stack spacing={1.5}>
                      {items.map((item) => (
                        <Card
                          key={item.url}
                          elevation={0}
                          sx={{
                            border: `1px solid ${grey[200]}`,
                            borderRadius: '10px',
                            '&:hover': { borderColor: blueGrey[300], boxShadow: 2 },
                            transition: 'all 0.15s',
                          }}>
                          <CardActionArea onClick={() => navigate(item.url)}>
                            <CardContent sx={{ px: 3, py: 2 }}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center">
                                <Stack spacing={0.5}>
                                  <Typography
                                    sx={{
                                      fontFamily: 'NotoSansKR',
                                      fontWeight: 700,
                                      fontSize: '15px',
                                      color: blueGrey[900],
                                    }}>
                                    {highlight(item.title, query)}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: 'NotoSansKR',
                                      fontSize: '13px',
                                      color: grey[600],
                                    }}>
                                    {highlight(item.description, query)}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: 'NotoSansKR',
                                      fontSize: '12px',
                                      color: grey[400],
                                      mt: 0.5,
                                    }}>
                                    {item.url}
                                  </Typography>
                                </Stack>
                                <ArrowForwardIosIcon
                                  sx={{ fontSize: '14px', color: grey[400], ml: 2, flexShrink: 0 }}
                                />
                              </Stack>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))}
                    </Stack>
                    <Divider sx={{ mt: 3 }} />
                  </Box>
                ))}
              </Stack>
            )}
          </>
        ) : (
          /* 검색어 없을 때: 카테고리 힌트 */
          <Stack spacing={2}>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'NotoSansKR', color: grey[500], mb: 1 }}>
              검색 가능한 카테고리
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {CATEGORY_ORDER.map((cat) => (
                <Chip
                  key={cat}
                  label={cat}
                  variant="outlined"
                  onClick={() => {
                    const keyword = SEARCH_DATA.find((d) => d.category === cat)?.keywords[0] || cat;
                    setQuery(keyword);
                    setSearchParams({ q: keyword });
                  }}
                  sx={{
                    fontFamily: 'NotoSansKR',
                    fontSize: '13px',
                    cursor: 'pointer',
                    borderColor: blueGrey[200],
                    color: blueGrey[700],
                    '&:hover': { backgroundColor: blueGrey[50] },
                  }}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Container>
    </Box>
  );
}

export default SearchPage;
